import { utcDay } from '@runkr/config/economy'
import type { RewardMsg } from '@runkr/schema'
import type { ComponentType } from 'preact'
import { Store } from '../net/store'
import type { Flow } from '../ui/flow'
import type { DailyApi } from './dailyApi'
import { makeDailyBox } from './DailyBox'
import { dailyView, withRewardTasks } from './dailyView'
import type { DailyView } from './dailyView'

/** Read side of a store (the session's flow). */
interface Readable<T> {
  get(): T
  subscribe(listener: (value: T) => void): () => void
}

export interface DailyDeps {
  /** `AuthSession.daily` once sign-in settles; null offline. */
  api: Promise<DailyApi | null>
  onReward: (listener: (msg: RewardMsg) => void) => () => void
  flow: Readable<Flow>
  /** Injectable clock for tests. */
  now?: () => Date
}

/**
 * Today's daily tasks (T2.5, D-053 slot `daily`): a store of `DailyView | null` (null offline
 * or until the first read) that also carries the `DailyBox` component, so the hub's slot
 * reader finds `daily.DailyBox`. Read after sign-in, on every `"reward"` (whose task progress
 * shows at once), on every return to the menu and whenever the box opens. A stale read never
 * overwrites a newer one.
 */
export class DailyStore extends Store<DailyView | null> {
  readonly DailyBox: ComponentType
  private api: DailyApi | null = null
  private seq = 0
  private readonly now: () => Date

  constructor(deps: DailyDeps) {
    super(null)
    this.now = deps.now ?? (() => new Date())
    this.DailyBox = makeDailyBox(this)
    let screen = deps.flow.get().screen
    deps.flow.subscribe((flow) => {
      const from = screen
      screen = flow.screen
      if (flow.screen === 'menu' && from !== 'menu') void this.refresh()
    })
    deps.onReward((msg) => this.onReward(msg))
    void deps.api.then((a) => {
      this.api = a
      if (a) void this.refresh()
    })
  }

  /** Read today's rows again (no-op offline). Resolves when done. */
  async refresh(): Promise<void> {
    const api = this.api
    if (!api) return
    const seq = ++this.seq
    const rows = await api.rows(utcDay(this.now()))
    if (seq !== this.seq) return
    this.set(dailyView(api.userId, this.now(), rows))
  }

  private onReward(msg: RewardMsg): void {
    const view = this.get()
    if (view && msg.tasks) this.set(withRewardTasks(view, msg.tasks))
    void this.refresh()
  }
}
